import { computed, effect, isSignal, model, ModelSignal, signal, Signal, untracked, WritableSignal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, AsyncValidatorFn, FormControl, FormControlStatus, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { debounceTime, map, throttleTime } from 'rxjs';
import { isNullOrUndefined } from 'src/app/shared/utils';

export function debounceSignal<T, PreR = T, PostR = T>(
  sourceSignal: Signal<T>,
  debounceTimeInMs: number,
  options?: { pre?: (from: T) => PreR, post?: (from: PreR) => PostR }
): Signal<PostR>;

export function debounceSignal<T, PreR = T, PostR = T>(
  sourceSignal: Signal<T>,
  debounceTimeInMs: number,
  options?: { pre?: (from: T) => PreR, post?: (from: PreR) => PostR }
): Signal<PostR> {
  let source$ = <any>toObservable(sourceSignal);

  if (options?.pre)
    source$ = source$.pipe(map(options.pre));

  let debounced$ = source$.pipe(debounceTime(debounceTimeInMs));

  if (options?.post)
    debounced$ = debounced$.pipe(map(options.post));

  return toSignal(<any>debounced$, {
    initialValue: <any>sourceSignal(),
  });
}

export function throttleSignal<T, PreR = T, PostR = T>(
  sourceSignal: Signal<T>,
  throttleTimeInMs: number,
  options?: { pre?: (from: T) => PreR, post?: (from: PreR) => PostR }
): Signal<PostR>;

export function throttleSignal<T, PreR = T, PostR = T>(
  sourceSignal: Signal<T>,
  throttleTimeInMs: number,
  options?: { pre?: (from: T) => PreR, post?: (from: PreR) => PostR }
): Signal<PostR> {
  let source$ = <any>toObservable(sourceSignal);

  if (options?.pre)
    source$ = source$.pipe(map(options.pre));

  let debounced$ = source$.pipe(throttleTime(throttleTimeInMs));

  if (options?.post)
    debounced$ = debounced$.pipe(map(options.post));

  return toSignal(<any>debounced$, {
    initialValue: <any>sourceSignal(),
  });
}

export interface FormControlBase {
  status: Signal<FormControlStatus>;
  valid: Signal<boolean>;
  invalid: Signal<boolean>;
  pending: Signal<boolean>;
  disabled: Signal<boolean>;
  enabled: Signal<boolean>;
  pristine: Signal<boolean>;
  dirty: Signal<boolean>;
  touched: Signal<boolean>;
  untouched: Signal<boolean>;

  errors: Signal<ValidationErrors | null>;
  setErrors(errors: ValidationErrors | null, opts?: { emitEvent?: boolean; });

  markAsTouched(opts?: { onlySelf?: boolean; emitEvent?: boolean; });
  markAsUntouched(opts?: { onlySelf?: boolean; emitEvent?: boolean; });
  markAsDirty(opts?: { onlySelf?: boolean; emitEvent?: boolean; });
  markAsPristine(opts?: { onlySelf?: boolean; emitEvent?: boolean; });
  markAsPending(opts?: { onlySelf?: boolean; emitEvent?: boolean; });

  disable(opts?: { onlySelf?: boolean; emitEvent?: boolean; });
  enable(opts?: { onlySelf?: boolean; emitEvent?: boolean; });
}

export type FormControlSignal<T> = ModelSignal<T> & FormControlBase & {
  control: FormControl<T>;

  setValue(value: T, options?: Object);
  patchValue(value: T, options?: Object);
  reset(value: T, options?: Object);
};

export type FormControlSignalOpts = { disabled?: Signal<boolean> };

export type FormControlInput<T> = (T extends WritableSignal<any> ? ReturnType<T> : T);

export function formControl<T>(value: FormControlInput<T>): FormControlSignal<T>;
export function formControl<T>(value: FormControlInput<T>, opts: FormControlSignalOpts & { nonNullable: true; }): FormControlSignal<T>;
export function formControl<T>(value: FormControlInput<T>, opts: FormControlSignalOpts & { initialValueIsDefault: true; }): FormControlSignal<T>;
export function formControl<T>(value: FormControlInput<T>, opts: FormControlSignalOpts, asyncValidator: AsyncValidatorFn | AsyncValidatorFn[]): FormControlSignal<T | null>;
export function formControl<T>(value: FormControlInput<T>, validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlSignalOpts | null, asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null): FormControlSignal<T | null>;
export function formControl<T>(model: WritableSignal<T>): FormControlSignal<T>;
export function formControl<T>(model: WritableSignal<T>, opts: FormControlSignalOpts & { nonNullable: true; }): FormControlSignal<T>;
export function formControl<T>(model: WritableSignal<T>, opts: FormControlSignalOpts & { initialValueIsDefault: true; }): FormControlSignal<T>;
export function formControl<T>(model: WritableSignal<T>, opts: FormControlSignalOpts, asyncValidator: AsyncValidatorFn | AsyncValidatorFn[]): FormControlSignal<T | null>;
export function formControl<T>(model: WritableSignal<T>, validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlSignalOpts | null, asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null): FormControlSignal<T | null>;
export function formControl<T>(value: T | WritableSignal<T>, ...args: any[]): FormControlSignal<T> {
  let opts = args.length > 1 ? args[1] as FormControlSignalOpts : null;

  let disabledSignal: Signal<boolean> = null;

  if (!isNullOrUndefined(opts) && typeof opts == 'object') {
    if ('disabled' in opts) {
      if (!isSignal(opts.disabled))
        throw new Error(`'disabled' must be a signal`);

      disabledSignal = opts.disabled;
    }
  }

  let valueIsSignal = value && (isSignal(value) || (typeof value === 'function' && 'set' in value));
  let initialValue  = untracked(() => valueIsSignal ? ((value as any)()) : value);

  let control = (new (FormControl as any)(initialValue, ...args)) as FormControl<T>;

  let valueSignal   = toSignal(control.valueChanges);
  let statusSignal  = toSignal(control.statusChanges);
  let dirtySignal   = signal(control.dirty);
  let touchedSignal = signal(control.touched);
  
  // Cannot call 'model' directly, so cast to any and call.
  let valueModel = <ModelSignal<T>>(
    (valueIsSignal && ('set' in (value as any)))
      ? value
      : ((model as any)(initialValue)));

  // Original ModelSignal set/update
  let modelSet    = valueModel.set;
  let modelUpdate = valueModel.update;

  // Override ModelSignal set/update so that the control receives changes made through it
  valueModel.set = v => {
    control.setValue(v);
    modelSet(v);
  };

  valueModel.update = fn => {
    let v = fn(untracked(() => valueModel)());

    control.setValue(v);
    modelSet(v);

    return v;
  };


  let errors = signal<ValidationErrors | null>(null);
  let result = valueModel as FormControlSignal<T>;

  result.control   = control;
  result.status    = statusSignal;
  result.valid     = computed(() => ({ _: [valueSignal(), statusSignal()], v: control.valid     }.v));
  result.invalid   = computed(() => ({ _: [valueSignal(), statusSignal()], v: control.invalid   }.v));
  result.pending   = computed(() => ({ _: [valueSignal(), statusSignal()], v: control.pending   }.v));
  result.disabled  = computed(() => ({ _: [valueSignal(), statusSignal()], v: control.disabled  }.v));
  result.enabled   = computed(() => ({ _: [valueSignal(), statusSignal()], v: control.enabled   }.v));
  result.pristine  = computed(() => ({ _: [valueSignal(), statusSignal(), dirtySignal()],   v: control.pristine }.v));
  result.dirty     = computed(() => ({ _: [valueSignal(), statusSignal(), dirtySignal()],   v: control.dirty    }.v));
  result.touched   = computed(() => ({ _: [valueSignal(), statusSignal(), touchedSignal()], v: control.touched   }.v));
  result.untouched = computed(() => ({ _: [valueSignal(), statusSignal(), touchedSignal()], v: control.untouched }.v));

  result.markAsTouched   = opts => { control.markAsTouched(opts);   touchedSignal.set(control.touched); }
  result.markAsUntouched = opts => { control.markAsUntouched(opts); touchedSignal.set(control.touched); }

  result.markAsDirty     = opts => { control.markAsDirty(opts);    dirtySignal.set(control.dirty); }
  result.markAsPristine  = opts => { control.markAsPristine(opts); dirtySignal.set(control.dirty); }

  result.markAsPending = opts => control.markAsPending(opts);
  result.disable       = opts => control.disable(opts);
  result.enable        = opts => control.enable(opts);
  
  result.errors    = errors;
  result.setErrors = (errors, opts) => control.setErrors(errors, opts);
  
  result.setValue = (v, opts) => {
    control.setValue(v, opts);
    valueModel.set(v);
    touchedSignal.set(control.touched);
    dirtySignal.set(control.dirty);
  };

  result.patchValue = (v, opts) => {
    control.patchValue(v, opts);
    valueModel.set(v);
    touchedSignal.set(control.touched);
    dirtySignal.set(control.dirty);
  };

  result.reset = (v, opts) => {
    control.reset(v, opts);
    valueModel.set(v);
    touchedSignal.set(control.touched);
    dirtySignal.set(control.dirty);
  };

  effect(() => {
    let _ = [valueSignal(), statusSignal()];
    errors.set(control.errors);
    modelSet(control.value);
    touchedSignal.set(control.touched);
    dirtySignal.set(control.dirty);
  });

  if (disabledSignal) {
    effect(() => {
      let disabled = disabledSignal();
      if (disabled == control.disabled)
        return;

      if (disabled)
        control.disable();
      else
        control.enable();
    });
  }

  return result;
}

export function formControls(...controls: FormControlSignal<unknown>[]) {
  controls = [...controls];

  return {
    controls,
    valid:     all(controls, x => x.valid()),
    invalid:   any(controls, x => x.invalid()),
    pending:   any(controls, x => x.pending()),
    pristine:  all(controls, x => x.pristine()),
    dirty:     any(controls, x => x.dirty()),
    touched:   any(controls, x => x.touched()),
    untouched: all(controls, x => x.untouched()),

    markAsDirty:     () => controls.forEach(x => x.markAsDirty()),
    markAsPending:   () => controls.forEach(x => x.markAsPending()),
    markAsPristine:  () => controls.forEach(x => x.markAsPristine()),
    markAsTouched:   () => controls.forEach(x => x.markAsTouched()),
    markAsUntouched: () => controls.forEach(x => x.markAsUntouched()),

    disable: () => controls.forEach(x => x.disable()),
    enable:  () => controls.forEach(x => x.enable()),
  };
}

export interface FormControlSignalLike<V = any> {
  readonly control:   AbstractControl;
  readonly valid:     Signal<boolean>;
  readonly invalid:   Signal<boolean>;
  readonly pending:   Signal<boolean>;
  readonly pristine:  Signal<boolean>;
  readonly dirty:     Signal<boolean>;
  readonly touched:   Signal<boolean>;
  readonly untouched: Signal<boolean>;
  readonly errors:    Signal<ValidationErrors | null>;
  
  set(values: V);
  reset(values: V);

  markAsPristine();
  markAsDirty();
  markAsTouched();
  markAsUntouched();
}

export type FormGroupSignal<T extends Record<string, FormControlSignalLike>> =
  Readonly<T> & FormControlSignalLike & {
    set(values: Partial<Record<keyof T, any>>);
    reset(values: Partial<Record<keyof T, any>>);
  };

export function formGroup<T extends Record<string, FormControlSignalLike>>(controls: T): FormGroupSignal<T>;
export function formGroup<T extends Record<string, FormControlSignalLike>>(controls: T, opts: FormControlSignalOpts & { nonNullable: true; }): FormGroupSignal<T>;
export function formGroup<T extends Record<string, FormControlSignalLike>>(controls: T, opts: FormControlSignalOpts & { initialValueIsDefault: true; }): FormGroupSignal<T>;
export function formGroup<T extends Record<string, FormControlSignalLike>>(controls: T, opts: FormControlSignalOpts, asyncValidator: AsyncValidatorFn | AsyncValidatorFn[]): FormGroupSignal<T>;
export function formGroup<T extends Record<string, FormControlSignalLike>>(controls: T, validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlSignalOpts | null, asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null): FormGroupSignal<T> {
  let formControls = {};
  
  Object.entries(controls)
      .filter(([k, v]) => 'control' in v && v.control instanceof AbstractControl)
      .forEach(([k, v]) => formControls[k] = v.control);

  let group = new FormGroup(formControls);

  let controlList = Object.values(controls);
  let controlMap  = { ...controls };

  return {
    control: group,

    debug: computed(() => {
      return Object.entries(controls).reduce((x, [k, v]) => ({
        ...x,
        [k]: {
          signal:    v,
          dirty:     v.dirty(),
          errors:    v.errors(),
          invalid:   v.invalid(),
          pending:   v.pending(),
          pristine:  v.pristine(),
          touched:   v.touched(),
          untouched: v.untouched(),
          valid:     v.valid(),
        }
      }), {});
    }),

    set(values: Partial<Record<keyof T, any>>) {
      Object.entries(values).forEach(([k, v]) => {
        if (!controlMap[k])
          throw new Error(`${k} not found in formGroup`);

        controlMap[k].set(v);
      });
    },
    
    reset(values: Partial<Record<keyof T, any>>) {
      Object.entries(values).forEach(([k, v]) => {
        if (!controlMap[k])
          throw new Error(`${k} not found in formGroup`);

        controlMap[k].reset(v);
      });
    },
    
    markAsPristine() {
      controlList.forEach(x => x.markAsPristine());
    },

    markAsDirty() {
      controlList.forEach(x => x.markAsDirty());
    }, 

    markAsTouched() {
      controlList.forEach(x => x.markAsTouched());
    },

    markAsUntouched() {
      controlList.forEach(x => x.markAsUntouched());
    },

    valid:     all(controlList, x => x.valid()),
    invalid:   any(controlList, x => x.invalid()),
    pending:   any(controlList, x => x.pending()),
    pristine:  all(controlList, x => x.pristine()),
    dirty:     any(controlList, x => x.dirty()),
    touched:   all(controlList, x => x.touched()),
    untouched: any(controlList, x => x.untouched()),
    
    errors: computed(() => {
      let errors = controlList.flatMap(x => x.errors());
      return errors.length ? errors : null;
    }),

    ...controls,
  };
}

function any<T>(controls: T[], fn: (control: T) => boolean) {
  return computed(() => controls.map(fn).some(x => !!x));
}

function all<T>(controls: T[], fn: (control: T) => boolean) {
  return computed(() => !controls.map(fn).some(x => !x));
}
